/**
 * User Settings Context
 * Provides app-wide access to user preferences (date format, time format, currency, etc.)
 * 
 * Usage:
 *   import { useUserSettings } from '@/contexts/UserSettingsContext';
 *   const { dateFormat, timeFormat, homeCurrency } = useUserSettings();
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/utils/api';
import { getCurrentUser } from '@/utils/auth';

// Resolve the effective theme ("light" or "dark") from a preference value
const resolveTheme = (preference) => {
  if (preference === 'dark') return 'dark';
  if (preference === 'light') return 'light';
  // "system" or undefined — use OS preference
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

// Apply theme class to document root
const applyTheme = (preference) => {
  const effective = resolveTheme(preference);
  const root = document.documentElement;
  if (effective === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  // Update mobile browser chrome color
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.content = effective === 'dark' ? '#030712' : '#f8fafc';
};

// Initialize theme early from localStorage to prevent flash
try {
  const cached = localStorage.getItem('userSettings');
  if (cached) {
    const parsed = JSON.parse(cached);
    applyTheme(parsed.theme || 'system');
  } else {
    // No saved preference — use system default
    applyTheme('system');
  }
} catch (e) {
  applyTheme('system');
}

// Default settings
const DEFAULT_SETTINGS = {
  home_currency: 'USD',
  distance_unit: 'miles',
  time_format: '12h',
  date_format: 'MM/DD/YYYY',
  theme: 'light',
  selected_traveler_id: null
};

const UserSettingsContext = createContext({
  settings: DEFAULT_SETTINGS,
  loading: true,
  error: null,
  refetchSettings: () => {},
  // Convenience getters
  dateFormat: 'MM/DD/YYYY',
  timeFormat: '12h',
  homeCurrency: 'USD',
  distanceUnit: 'miles',
  use24Hour: false,
  useDDMMYYYY: false
});

export const UserSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      // Only fetch if user is authenticated
      const user = getCurrentUser();
      if (!user?.id) {
        setLoading(false);
        return;
      }
      
      const response = await api.get(`/user-settings`);
      const newSettings = {
        ...DEFAULT_SETTINGS,
        ...response.data
      };
      setSettings(newSettings);
      setError(null);
      
      // Cache settings to localStorage for synchronous access in utilities
      try {
        localStorage.setItem('userSettings', JSON.stringify({
          date_format: newSettings.date_format,
          time_format: newSettings.time_format,
          distance_unit: newSettings.distance_unit,
          home_currency: newSettings.home_currency,
          theme: newSettings.theme
        }));
      } catch (e) {
        // Ignore localStorage errors
      }

      // Apply theme to document
      applyTheme(newSettings.theme);

      // Fetch vendors once so user-supplied logo domains (logo_domain) are
      // registered with the VendorLogo component everywhere in the app — not
      // only on the Vendors page. Non-fatal if it fails.
      try {
        const vendorsRes = await api.get(`/vendors`);
        const { registerVendorDomainOverrides } = await import("@/utils/vendorLogos");
        registerVendorDomainOverrides(vendorsRes.data || []);
      } catch (e) {
        // Ignore — fall back to static VENDOR_DOMAINS
      }
    } catch (err) {
      // Don't treat 404 (no settings yet) as an error
      if (err.response?.status !== 404) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch settings on mount and when user changes
  useEffect(() => {
    fetchSettings();
    
    // Listen for user login/logout
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        fetchSettings();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchSettings]);

  // Listen for OS dark mode changes when theme is "system"
  useEffect(() => {
    const currentTheme = settings.theme || 'system';
    if (currentTheme !== 'system') return;

    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;

    const handler = () => applyTheme('system');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [settings.theme]);

  // Computed values for convenience
  const effectiveTheme = resolveTheme(settings.theme || 'system');
  const value = {
    settings,
    loading,
    error,
    refetchSettings: fetchSettings,
    // Convenience getters
    dateFormat: settings.date_format || 'MM/DD/YYYY',
    timeFormat: settings.time_format || '12h',
    homeCurrency: settings.home_currency || 'USD',
    distanceUnit: settings.distance_unit || 'miles',
    theme: settings.theme || 'system',
    effectiveTheme,
    isDark: effectiveTheme === 'dark',
    use24Hour: settings.time_format === '24h',
    useDDMMYYYY: settings.date_format === 'DD/MM/YYYY'
  };

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
    </UserSettingsContext.Provider>
  );
};

export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (!context) {
    // Return defaults if used outside provider (for backwards compatibility)
    return {
      settings: DEFAULT_SETTINGS,
      loading: false,
      error: null,
      refetchSettings: () => {},
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12h',
      homeCurrency: 'USD',
      distanceUnit: 'miles',
      use24Hour: false,
      useDDMMYYYY: false
    };
  }
  return context;
};

/**
 * Get user settings synchronously from localStorage cache
 * Useful for utilities that can't use hooks
 * Falls back to defaults if not available
 */
export const getUserSettingsSync = () => {
  try {
    const cached = localStorage.getItem('userSettings');
    if (cached) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(cached) };
    }
  } catch (e) {
  }
  return DEFAULT_SETTINGS;
};

export default UserSettingsContext;
